import React, { useState, useRef, useEffect } from 'react';
import { Box, Typography, Stack, CircularProgress } from '@mui/material';
import { useDispatch } from 'react-redux';
import { AppDispatch } from 'store';
import { sendMessage, getMessages, markAsRead, clearMessages } from 'store/slices/candidate/text-messages-slice';
import { useAppSelector } from 'helpers/hooks';
import { getInitialsFromName, stringToColor } from 'shared/utils';
import { useNotification } from 'Modules/Core/Notification';
import { fetchAndGenerateCandidateAiSummary } from "store/slices/employer/applicants/applicants-list-slice";

interface ApplicantTextMessagesProps {
  candidateEmployerJobId?: string | number;
}

export const ApplicantTextMessages: React.FC<ApplicantTextMessagesProps> = ({candidateEmployerJobId,}) => {
  const dispatch = useDispatch<AppDispatch>();
  const { 
    messages, 
    loadingTextMessagesStatus,
    loadingTextMessagesResponse,
    sendingTextMessageStatus,
    sendingTextMessageResponse,
    unreadCount 
  } = useAppSelector((state) => state.candidate.textMessages);
  const { applicantDetails } = useAppSelector((state) => state.employer.applicants.applicantProfile);
  const { aiCandidateSummary } = useAppSelector((state) => state.employer.applicants.applicantsList);
  const [message, setMessage] = useState('');
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const notification = useNotification();

  const suggestedMessages = [
    "Hi! We're impressed with your application. Would you be available for an interview next week?",
    "Hello! Thanks for applying. When would be a good time to discuss the role?",
    "Hi there! We'd love to learn more about your experience. What's your availability like?",
  ];

  const handleSuggestedMessage = (message: string) => {
    setMessage(message);
  };

  const handleSendMessage = () => {
    if (!message.trim() || sendingTextMessageStatus === 'pending' || !candidateEmployerJobId) return;
    
    dispatch(sendMessage({
      candidateEmployerJobId: candidateEmployerJobId.toString(),
      message: message.trim()
    })).unwrap()
      .then(() => {
        setMessage('');
        dispatch(getMessages({ candidateEmployerJobId: candidateEmployerJobId.toString() }));
      })
      .catch((error) => {
        notification.displayNotification({
          open: true,
          type: 'error',
          message: 'Failed to send message. Please try again later.'
        });
      });
  };

  useEffect(() => {
    if (candidateEmployerJobId) {
      dispatch(getMessages({ candidateEmployerJobId: candidateEmployerJobId.toString() }));
      dispatch(markAsRead({ candidateEmployerJobId: candidateEmployerJobId.toString() }));
    }

    return () => {
      dispatch(clearMessages());
    };
  }, [dispatch, candidateEmployerJobId]);

  useEffect(() => {
    if (applicantDetails?.resumeId && applicantDetails?.candidateEmployerJobId && !aiCandidateSummary) {
      dispatch(
        fetchAndGenerateCandidateAiSummary({
          resume_id: applicantDetails.resumeId,
          candidate_id: applicantDetails.candidateId,
          candidate_employerjobs_id: applicantDetails.candidateEmployerJobId
        })
      );
    }
  }, [dispatch, applicantDetails, aiCandidateSummary]);

  useEffect(() => {
    if (messages.length > 0) {
      scrollToBottom();
    }
  }, [messages]);

  useEffect(() => {
    if (loadingTextMessagesStatus === 'success' && messages.length > 0) {
      scrollToBottom();
    }
  }, [loadingTextMessagesStatus, messages.length]);

  useEffect(() => {
    if (sendingTextMessageStatus === 'failed' && sendingTextMessageResponse) {
      notification.displayNotification({
        open: true,
        type: 'error',
        message: sendingTextMessageResponse
      });
    }
  }, [sendingTextMessageStatus, sendingTextMessageResponse, notification]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const handleKeyPress = (event: React.KeyboardEvent) => {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      if (sendingTextMessageStatus !== 'pending') {
        handleSendMessage();
      }
    }
  };

  if (loadingTextMessagesStatus === 'failed' && loadingTextMessagesResponse) {
    notification.displayNotification({
      open: true,
      type: 'error',
      message: loadingTextMessagesResponse
    });
  }

  if (loadingTextMessagesStatus === 'pending' && messages.length === 0) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" height="100%">
        <CircularProgress />
      </Box>
    );
  }

  const applicantName = applicantDetails?.fullName || `${applicantDetails?.firstName} ${applicantDetails?.lastName}`;
  const applicantInitials = getInitialsFromName(applicantDetails?.fullName || applicantDetails?.email || "SK", true, true);
  const avatarBackgroundColor = stringToColor(applicantInitials);

  // if (loadingTextMessagesStatus === 'success' && messages.length === 0) {
  //   return (
  //     <Stack>
  //       <ShPaper variant='outlined' headerSection borderRadius={0}>
  //         <Typography variant="h6">Applicant Text Messages</Typography>
  //         <Typography variant="body2" color='text.secondary'>Text candidates directly to get to know them better.</Typography>
  //         {!aiCandidateSummary?.phone_number && (
  //           <Box mt={2}>
  //             <Alert severity="warning">
  //               Warning: This candidate does not have a phone number. You will not be able to send text messages.
  //             </Alert>
  //           </Box>
  //         )}
  //       </ShPaper>
  //       <Grid container>
  //         <Grid item xs={3}>
  //           <ApplicantDetailsPanel
  //             applicantDetails={applicantDetails}
  //             aiCandidateSummary={aiCandidateSummary}
  //             unreadCount={unreadCount}
  //           />
  //         </Grid>
          
  //         <Grid item xs={9}>
  //           <ShPaper variant="outlined" noBorderTop height="100%">
  //             <Box p={2} borderBottom={1} borderColor="divider">
  //               <Box display="flex" alignItems="center" gap={2}>
  //                 <ShAvatar height={45} width={45} backgroundColor={avatarBackgroundColor}>
  //                   <Typography variant="h6">
  //                     {applicantInitials}
  //                   </Typography>
  //                 </ShAvatar>
  //                 <Box flex={1}>
  //                   <Box display="flex" alignItems="center" gap={2}>
  //                     <Typography variant="h6">
  //                       {toTitleCase(applicantName)}
  //                     </Typography>
  //                     {(!aiCandidateSummary?.current_job_title && 
  //                       (!aiCandidateSummary?.previous_experience || aiCandidateSummary.previous_experience.length === 0)) && (
  //                       <ExperienceChip
  //                         icon={<WorkIcon />}
  //                         label="No Previous Experience Found"
  //                         size="small"
  //                       />
  //                     )}
  //                   </Box>
  //                   <Stack direction="row" spacing={1} flexWrap="wrap" mt={1}>
  //                     {aiCandidateSummary?.phone_number && (
  //                       <PhoneChip
  //                         icon={<PhoneIcon />}
  //                         label={aiCandidateSummary.phone_number}
  //                         size="small"
  //                       />
  //                     )}
  //                     {aiCandidateSummary?.current_job_title && (
  //                       <ExperienceChip
  //                         icon={<WorkIcon />}
  //                         label={
  //                           `${aiCandidateSummary.current_job_title} at ${aiCandidateSummary.current_employer || 'Unknown Company'}`
  //                         }
  //                         size="small"
  //                       />
  //                     )}
  //                     {aiCandidateSummary && aiCandidateSummary?.total_years_experience && aiCandidateSummary?.total_years_experience > 0 && (
  //                       <YearsChip
  //                         icon={<AccessTimeIcon />}
  //                         label={`${aiCandidateSummary?.total_years_experience} years experience`}
  //                         size="small"
  //                       />
  //                     )}
  //                   </Stack>
  //                 </Box>
  //               </Box>
  //             </Box>
              
  //             <MessagesContainer>
  //               <EmptyStateContainer>
  //                 <ChatBubbleOutlineIcon color="primary" fontSize="large" />
  //                 <Typography variant="h6" color="primary">
  //                   No Messages Yet
  //                 </Typography>
  //                 <Typography variant="body1" color="textSecondary" maxWidth={650}>
  //                   Start the conversation with {applicantName.split(' ')[0]}! Send a message to discuss the role, schedule an interview, or learn more about their experience.
  //                 </Typography>
                  
  //                 <Box mt={3} width="100%" maxWidth={500}>
  //                   <Typography variant="subtitle2" color="textSecondary" mb={2}>
  //                     Suggested messages:
  //                   </Typography>
  //                   <Stack spacing={2}>
  //                     {suggestedMessages.map((msg, index) => (
  //                       <SuggestedMessage key={index} onClick={() => handleSuggestedMessage(msg)}>
  //                         <Typography variant="body1">{msg}</Typography>
  //                       </SuggestedMessage>
  //                     ))}
  //                   </Stack>
  //                 </Box>
  //               </EmptyStateContainer>
  //             </MessagesContainer>

  //             <InputContainer>
  //               <ShTextFieldV2
  //                 fullWidth
  //                 multiline
  //                 maxRows={4}
  //                 value={message}
  //                 onChange={(e) => setMessage(e.target.value)}
  //                 onKeyPress={handleKeyPress}
  //                 placeholder="Type your message..."
  //                 disabled={sendingTextMessageStatus === 'pending'}
  //               />
  //               <IconButton
  //                 color="primary"
  //                 onClick={handleSendMessage}
  //                 disabled={!message.trim() || sendingTextMessageStatus === 'pending'}
  //               >
  //                 {sendingTextMessageStatus === 'pending' ? (
  //                   <CircularProgress size={24} />
  //                 ) : (
  //                   <SendIcon />
  //                 )}
  //               </IconButton>
  //             </InputContainer>
  //           </ShPaper>
  //         </Grid>
  //       </Grid>
  //     </Stack>
  //   );
  // }

  return (

    <>

      {/* <Stack>
        <ShPaper variant='outlined' headerSection borderRadius={0}>
          <Typography variant="h6">Applicant Text Messages</Typography>
          <Typography variant="body2" color='text.secondary'>Text candidates directly to get to know them better.</Typography>
          {!aiCandidateSummary?.phone_number && (
            <Box mt={2}>
              <Alert severity="warning">
                Warning: This candidate does not have a phone number. You will not be able to send text messages.
              </Alert>
            </Box>
          )}
        </ShPaper>
        <Grid container>
          <Grid item xs={3}>
            <ApplicantDetailsPanel
              applicantDetails={applicantDetails}
              aiCandidateSummary={aiCandidateSummary}
              unreadCount={unreadCount} />
          </Grid>
          <Grid item xs={9}>
            <ShPaper variant="outlined" noBorderTop height="100%">
              <Box p={2} borderBottom={1} borderColor="divider">
                <Box display="flex" alignItems="center" gap={2}>
                  <ShAvatar height={45} width={45} backgroundColor={avatarBackgroundColor}>
                    <Typography variant="h6">
                      {applicantInitials}
                    </Typography>
                  </ShAvatar>
                  <Box flex={1}>
                    <Box display="flex" alignItems="center" gap={2}>
                      <Typography variant="h6">
                        {toTitleCase(applicantName)}
                      </Typography>
                      {(!aiCandidateSummary?.current_job_title &&
                        (!aiCandidateSummary?.previous_experience || aiCandidateSummary.previous_experience.length === 0)) && (
                          <ExperienceChip
                            icon={<WorkIcon />}
                            label="No Previous Experience Found"
                            size="small" />
                        )}
                    </Box>
                    <Stack direction="row" spacing={1} flexWrap="wrap" mt={1}>
                      {aiCandidateSummary?.phone_number && (
                        <PhoneChip
                          icon={<PhoneIcon />}
                          label={aiCandidateSummary.phone_number}
                          size="small" />
                      )}
                      {aiCandidateSummary?.current_job_title && (
                        <ExperienceChip
                          icon={<WorkIcon />}
                          label={`${aiCandidateSummary.current_job_title} at ${aiCandidateSummary.current_employer || 'Unknown Company'}`}
                          size="small" />
                      )}
                      {aiCandidateSummary && aiCandidateSummary?.total_years_experience && aiCandidateSummary?.total_years_experience > 0 && (
                        <YearsChip
                          icon={<AccessTimeIcon />}
                          label={`${aiCandidateSummary?.total_years_experience} years experience`}
                          size="small" />
                      )}
                    </Stack>
                  </Box>
                </Box>
              </Box>

              <MessagesContainer>
                {groupMessagesByDate(messages).map((group) => (
                  <React.Fragment key={group.date.toISOString()}>
                    <DateSeparator>
                      <Typography variant="caption" color="text.secondary">
                        {formatDateSeparator(group.date)}
                      </Typography>
                    </DateSeparator>
                    {group.messages.map((message) => (
                      <MessageBox
                        key={message.id}
                        isRecruiter={message.sender === 'recruiter'}
                      >
                        <Box display="flex" alignItems="center" gap={1} mb={0.5}>
                          {message.sender === 'recruiter' ? (
                            <ShAvatar height={24} width={24} backgroundColor="primary.main">
                              <Typography variant="caption">R</Typography>
                            </ShAvatar>
                          ) : (
                            <ShAvatar height={24} width={24} backgroundColor={avatarBackgroundColor}>
                              <Typography variant="caption">{applicantInitials}</Typography>
                            </ShAvatar>
                          )}
                          <Typography variant="caption" color="text.secondary">
                            {message.sender === 'recruiter' ? 'Recruiter' : applicantName}
                          </Typography>
                        </Box>
                        {message.sender === 'recruiter' ? (
                          <>
                            <RecruiterMessageBubble>
                              <Typography variant="body1">
                                {message.content}
                              </Typography>
                              {message.status === 'sent' && (
                                <Box display="flex" justifyContent="flex-end" mt={0.5}>
                                  <CheckCircleIcon fontSize="small" />
                                </Box>
                              )}
                            </RecruiterMessageBubble>
                            <MessageTime>
                              {formatMessageTime(message.timestamp)}
                            </MessageTime>
                          </>
                        ) : (
                          <>
                            <ApplicantMessageBubble>
                              <Typography variant="body1">
                                {message.content}
                              </Typography>
                            </ApplicantMessageBubble>
                            <MessageTime>
                              {formatMessageTime(message.timestamp)}
                            </MessageTime>
                          </>
                        )}
                      </MessageBox>
                    ))}
                  </React.Fragment>
                ))}
                <div ref={messagesEndRef} />
              </MessagesContainer>

              <InputContainer>
                <ShTextFieldV2
                  fullWidth
                  multiline
                  maxRows={4}
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  onKeyPress={handleKeyPress}
                  placeholder="Type your message..."
                  disabled={sendingTextMessageStatus === 'pending'} />
                <IconButton
                  color="primary"
                  onClick={handleSendMessage}
                  disabled={!message.trim() || sendingTextMessageStatus === 'pending'}
                >
                  {sendingTextMessageStatus === 'pending' ? (
                    <CircularProgress size={24} />
                  ) : (
                    <SendIcon />
                  )}
                </IconButton>
              </InputContainer>
            </ShPaper>
          </Grid>
        </Grid>
      </Stack> */}
      <Stack>
        <Typography variant="h6" color="text.secondary">Coming Very Soon!</Typography>
      </Stack>
    </>
  );
}; 